import React, { useState, useEffect } from "react";
import "./UsageLimit.css";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";

const UsageLimitComponent = (props) => {
  const {
    loading,
    usageLimitData,
    isEditMode,
    formData,
    handleBackClick,
    handleEditClick,
    handleCancelClick,
    handleInputChange,
    handleUpdateLimit,
    corporateId,
    accountType
  } = props;

  const [bpLimits, setBpLimits] = useState([]);
  const [tagLimits, setTagLimits] = useState([]);
  const [localUsageLimitRequired, setLocalUsageLimitRequired] = useState(false);
  const [savedBpLimits, setSavedBpLimits] = useState([]);
  const [savedTagLimits, setSavedTagLimits] = useState([]);
  const [userRoleId, setUserRoleId] = useState(null);

  // Check if account type is WALLET
  const isWalletAccount = accountType === "WALLET";

  // Get user role from localStorage
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("data"));
      if (userData && userData.details && userData.details.userRole) {
        setUserRoleId(userData.details.userRole.role_id);
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
    }
  }, []);

  // Check if user can perform actions (hide for role_id 15)
  const canPerformActions = userRoleId !== 15 && userRoleId !== null;

  useEffect(() => {
    if (formData) {
      setLocalUsageLimitRequired(formData.usageLimitRequired || false);
      setBpLimits(formData.bpLimits || []);
      setTagLimits(formData.tagLimits || []);
      if (formData.bpLimits && formData.bpLimits.length > 0) {
        setSavedBpLimits(JSON.parse(JSON.stringify(formData.bpLimits)));
      }
      if (formData.tagLimits && formData.tagLimits.length > 0) {
        setSavedTagLimits(JSON.parse(JSON.stringify(formData.tagLimits)));
      }
    }
  }, [formData]);

  const getFrequencyDisplay = (frequency) => {
    if (!frequency) return "N/A";
    if (frequency === "HOURS") return "Hour";
    if (frequency === "DAYS") return "Day";
    if (frequency === "MONTHS") return "Month";
    return frequency;
  };

  const getLimitTypeDisplay = (limitOn) => {
    if (limitOn === "AMOUNT") return "Amount";
    if (limitOn === "TRANSACTION") return "Transaction";
    return limitOn || "N/A";
  };

  const getDurationUnit = (frequency) => {
    if (frequency === "HOURS") return "Hour(s)";
    if (frequency === "DAYS") return "Day(s)";
    if (frequency === "MONTHS") return "Month(s)";
    return "";
  };

  // BP Limits handlers
  const hasBothBpLimitTypes = () => {
    const hasAmount = bpLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = bpLimits.some(limit => limit.limitOn === "TRANSACTION");
    return hasAmount && hasTransaction;
  };

  const canAddMoreBpLimits = () => {
    if (bpLimits.length >= 2) return false;
    return !hasBothBpLimitTypes();
  };

  // Tag Limits handlers (only for non-WALLET accounts)
  const hasBothTagLimitTypes = () => {
    const hasAmount = tagLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = tagLimits.some(limit => limit.limitOn === "TRANSACTION");
    return hasAmount && hasTransaction;
  };

  const canAddMoreTagLimits = () => {
    if (tagLimits.length >= 2) return false;
    return !hasBothTagLimitTypes();
  };

  const handleUsageLimitRequiredChange = (value) => {
    const isRequired = value === "true";
    setLocalUsageLimitRequired(isRequired);
    handleInputChange("usageLimitRequired", isRequired);
  };

  // BP Limit handlers
  const handleBpLimitOnChange = (index, value) => {
    const updatedLimits = [...bpLimits];
    updatedLimits[index] = { ...updatedLimits[index], limitOn: value };
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  const handleBpFrequencyChange = (index, value) => {
    const updatedLimits = [...bpLimits];
    updatedLimits[index] = { ...updatedLimits[index], frequency: value, duration: "1" };
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  const handleBpDurationChange = (index, value) => {
    const updatedLimits = [...bpLimits];
    updatedLimits[index] = { ...updatedLimits[index], duration: value };
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  const handleBpValueChange = (index, e) => {
    const value = e.target.value;
    const updatedLimits = [...bpLimits];
    updatedLimits[index] = { ...updatedLimits[index], value: value };
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  const handleAddNewBpLimit = () => {
    const hasAmount = bpLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = bpLimits.some(limit => limit.limitOn === "TRANSACTION");
    
    let newLimitType = "AMOUNT";
    if (hasAmount && !hasTransaction) {
      newLimitType = "TRANSACTION";
    } else if (!hasAmount && hasTransaction) {
      newLimitType = "AMOUNT";
    }
    
    const newLimit = { limitOn: newLimitType, frequency: "DAYS", duration: "1", value: "" };
    const updatedLimits = [...bpLimits, newLimit];
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  const handleRemoveBpLimit = (index) => {
    const updatedLimits = bpLimits.filter((_, i) => i !== index);
    setBpLimits(updatedLimits);
    handleInputChange("bpLimits", updatedLimits);
  };

  // Tag Limit handlers (only for non-WALLET accounts)
  const handleTagLimitOnChange = (index, value) => {
    const updatedLimits = [...tagLimits];
    updatedLimits[index] = { ...updatedLimits[index], limitOn: value };
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleTagFrequencyChange = (index, value) => {
    const updatedLimits = [...tagLimits];
    updatedLimits[index] = { ...updatedLimits[index], frequency: value, duration: "1" };
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleTagDurationChange = (index, value) => {
    const updatedLimits = [...tagLimits];
    updatedLimits[index] = { ...updatedLimits[index], duration: value };
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleTagValueChange = (index, e) => {
    const value = e.target.value;
    const updatedLimits = [...tagLimits];
    updatedLimits[index] = { ...updatedLimits[index], value: value };
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleAddNewTagLimit = () => {
    const hasAmount = tagLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = tagLimits.some(limit => limit.limitOn === "TRANSACTION");
    
    let newLimitType = "AMOUNT";
    if (hasAmount && !hasTransaction) {
      newLimitType = "TRANSACTION";
    } else if (!hasAmount && hasTransaction) {
      newLimitType = "AMOUNT";
    }
    
    const newLimit = { limitOn: newLimitType, frequency: "DAYS", duration: "1", value: "" };
    const updatedLimits = [...tagLimits, newLimit];
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleRemoveTagLimit = (index) => {
    const updatedLimits = tagLimits.filter((_, i) => i !== index);
    setTagLimits(updatedLimits);
    handleInputChange("tagLimits", updatedLimits);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const handleSaveClick = () => {
    handleUpdateLimit();
  };

  const isUsageLimitRequired = localUsageLimitRequired === true;

  if (loading) {
    return (
      <div className="usage-limit__container">
        <div className="usage-limit__sidebar">
          <Sidebar />
        </div>
        <div className="usage-limit__main">
          <div className="usage-limit__loading">Loading Usage Limits...</div>
        </div>
      </div>
    );
  }

  // Render Limit Section Component - Only shows if limits exist
  const renderLimitSection = (title, limits, handlers, canAddMore, type) => {
    const {
      onLimitOnChange,
      onFrequencyChange,
      onDurationChange,
      onValueChange,
      onAddNew,
      onRemove
    } = handlers;

    // Don't render anything if no limits and not in edit mode
    if (!isEditMode && limits.length === 0) {
      return null;
    }

    return (
      <div className="usage-limit__full-width">
        <div className="usage-limit__limits-header">
          <div className="usage-limit__field-label">{title} CONFIGURATION</div>
          {/* Show "Set New Limit" button only in edit mode AND when isUsageLimitRequired is true */}
          {isEditMode && canAddMore && canPerformActions && isUsageLimitRequired && (
            <button className="usage-limit__add-limit-btn" onClick={onAddNew} type="button">
              + Set New {title} Limit
            </button>
          )}
        </div>
        
        {limits && limits.length > 0 ? (
          limits.map((limit, index) => (
            <div key={index} className="usage-limit__limit-item">
              <div className="usage-limit__limit-header">
                <div className="usage-limit__limit-title">
                  {limit.limitOn === "AMOUNT" ? `💰 Amount Limit (${title})` : `🔄 Transaction Limit (${title})`}
                </div>
                {isEditMode && limits.length > 1 && canPerformActions && (
                  <button className="usage-limit__remove-limit-btn" onClick={() => onRemove(index)} type="button">
                    ✕ Remove
                  </button>
                )}
              </div>
              
              <div className="usage-limit__limit-fields">
                {/* Limit Type */}
                <div className="usage-limit__limit-field">
                  <div className="usage-limit__field-label-small">LIMIT TYPE</div>
                  {isEditMode ? (
                    <div className="usage-limit__radio-group-small">
                      <label className="usage-limit__radio-label-small">
                        <input
                          type="radio"
                          name={`${title}_limitOn_${index}`}
                          value="AMOUNT"
                          checked={limit.limitOn === "AMOUNT"}
                          onChange={(e) => onLimitOnChange(index, e.target.value)}
                          className="usage-limit__radio-input-small"
                          disabled={!canPerformActions || (limit.limitOn === "AMOUNT" && limits.some(l => l.limitOn === "TRANSACTION"))}
                        />
                        <span className="usage-limit__radio-custom-small"></span>
                        <span className="usage-limit__radio-text-small">Amount</span>
                      </label>
                      <label className="usage-limit__radio-label-small">
                        <input
                          type="radio"
                          name={`${title}_limitOn_${index}`}
                          value="TRANSACTION"
                          checked={limit.limitOn === "TRANSACTION"}
                          onChange={(e) => onLimitOnChange(index, e.target.value)}
                          className="usage-limit__radio-input-small"
                          disabled={!canPerformActions || (limit.limitOn === "TRANSACTION" && limits.some(l => l.limitOn === "AMOUNT"))}
                        />
                        <span className="usage-limit__radio-custom-small"></span>
                        <span className="usage-limit__radio-text-small">Transaction</span>
                      </label>
                    </div>
                  ) : (
                    <div className="usage-limit__type-badge">
                      {getLimitTypeDisplay(limit.limitOn)}
                    </div>
                  )}
                </div>

                {/* Frequency */}
                <div className="usage-limit__limit-field">
                  <div className="usage-limit__field-label-small">FREQUENCY</div>
                  {isEditMode ? (
                    <div className="usage-limit__radio-group-small">
                      <label className="usage-limit__radio-label-small">
                        <input
                          type="radio"
                          name={`${title}_frequency_${index}`}
                          value="HOURS"
                          checked={limit.frequency === "HOURS"}
                          onChange={(e) => onFrequencyChange(index, e.target.value)}
                          className="usage-limit__radio-input-small"
                          disabled={!canPerformActions}
                        />
                        <span className="usage-limit__radio-custom-small"></span>
                        <span className="usage-limit__radio-text-small">Hour</span>
                      </label>
                      <label className="usage-limit__radio-label-small">
                        <input
                          type="radio"
                          name={`${title}_frequency_${index}`}
                          value="DAYS"
                          checked={limit.frequency === "DAYS"}
                          onChange={(e) => onFrequencyChange(index, e.target.value)}
                          className="usage-limit__radio-input-small"
                          disabled={!canPerformActions}
                        />
                        <span className="usage-limit__radio-custom-small"></span>
                        <span className="usage-limit__radio-text-small">Day</span>
                      </label>
                      <label className="usage-limit__radio-label-small">
                        <input
                          type="radio"
                          name={`${title}_frequency_${index}`}
                          value="MONTHS"
                          checked={limit.frequency === "MONTHS"}
                          onChange={(e) => onFrequencyChange(index, e.target.value)}
                          className="usage-limit__radio-input-small"
                          disabled={!canPerformActions}
                        />
                        <span className="usage-limit__radio-custom-small"></span>
                        <span className="usage-limit__radio-text-small">Month</span>
                      </label>
                    </div>
                  ) : (
                    <div className="usage-limit__frequency-badge">
                      {getFrequencyDisplay(limit.frequency)}
                    </div>
                  )}
                </div>

                {/* Duration */}
                <div className="usage-limit__limit-field">
                  <div className="usage-limit__field-label-small">DURATION</div>
                  {isEditMode ? (
                    <div className="usage-limit__duration-wrapper">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={limit.duration || ""}
                        onChange={(e) => onDurationChange(index, e.target.value)}
                        onWheel={handleWheel}
                        onKeyDown={handleKeyDown}
                        className="usage-limit__duration-field"
                        placeholder="Enter duration"
                        disabled={!canPerformActions}
                      />
                      <span className="usage-limit__duration-unit">
                        {getDurationUnit(limit.frequency)}
                      </span>
                    </div>
                  ) : (
                    <div className="usage-limit__duration-value">
                      {limit.duration ? `${limit.duration} ${getFrequencyDisplay(limit.frequency)}${parseInt(limit.duration) > 1 ? 's' : ''}` : "N/A"}
                    </div>
                  )}
                </div>

                {/* Value */}
                <div className="usage-limit__limit-field">
                  <div className="usage-limit__field-label-small">LIMIT VALUE</div>
                  {isEditMode ? (
                    <div className="usage-limit__input-wrapper">
                      <span className="usage-limit__currency">₹</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={limit.value || ""}
                        onChange={(e) => onValueChange(index, e)}
                        onWheel={handleWheel}
                        onKeyDown={handleKeyDown}
                        className="usage-limit__input-field"
                        placeholder="Enter limit value"
                        disabled={!canPerformActions}
                      />
                    </div>
                  ) : (
                    <div className="usage-limit__limit-value">
                      ₹{limit.value || "N/A"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : null}
      </div>
    );
  };

  const bpHandlers = {
    onLimitOnChange: handleBpLimitOnChange,
    onFrequencyChange: handleBpFrequencyChange,
    onDurationChange: handleBpDurationChange,
    onValueChange: handleBpValueChange,
    onAddNew: handleAddNewBpLimit,
    onRemove: handleRemoveBpLimit
  };

  const tagHandlers = {
    onLimitOnChange: handleTagLimitOnChange,
    onFrequencyChange: handleTagFrequencyChange,
    onDurationChange: handleTagDurationChange,
    onValueChange: handleTagValueChange,
    onAddNew: handleAddNewTagLimit,
    onRemove: handleRemoveTagLimit
  };

  return (
    <div className="usage-limit__container">
      <div className="usage-limit__sidebar">
        <Sidebar />
      </div>

      <div className="usage-limit__main">
        <div className="usage-limit__header">
          <div className="usage-limit__header-left">
            <button className="usage-limit__back-btn" onClick={handleBackClick}>
              ←
            </button>
            <h2 className="usage-limit__title">Usage Limit</h2>
            <span className="usage-limit__corp-id">Corporate ID: {corporateId}</span>
            {accountType && (
              <span className={`usage-limit__account-type ${accountType === "WALLET" ? "wallet-badge" : "tag-badge"}`}>
                {accountType === "WALLET" ? "Wallet Account" : "Tag Based Account"}
              </span>
            )}
          </div>
          
          <div className="usage-limit__header-right">
            {isEditMode && (
              <div className="usage-limit__edit-actions">
                <button className="usage-limit__cancel-btn" onClick={handleCancelClick}>
                  Cancel
                </button>
                <button className="usage-limit__save-btn" onClick={handleSaveClick}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="usage-limit__content">
          <div className="usage-limit__page-header">
            <h1 className="usage-limit__page-title">Usage Limit</h1>
            <p className="usage-limit__page-subtitle">
              Define spending thresholds and transaction volume controls for {isWalletAccount ? "BP users" : "BP users and Tags"} of this corporate account.
            </p>
          </div>

          <div className="usage-limit__card">
            <div className="usage-limit__card-header">
              <div className="usage-limit__corp-info">
                <div className="usage-limit__info-label">CORPORATE</div>
                <div className="usage-limit__corp-name">{`Corporate ${corporateId}`}</div>
              </div>
            </div>

            <div className="usage-limit__card-body">
              <div className="usage-limit__grid">
                {/* Usage Limit Required */}
                <div className="usage-limit__grid-item usage-limit__full-width">
                  <div className="usage-limit__field-label">USAGE LIMIT REQUIRED</div>
                  {isEditMode ? (
                    <div className="usage-limit__radio-group">
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="true"
                          checked={localUsageLimitRequired === true}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                          disabled={!canPerformActions}
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">Yes</span>
                      </label>
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="false"
                          checked={localUsageLimitRequired === false}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                          disabled={!canPerformActions}
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">No</span>
                      </label>
                    </div>
                  ) : (
                    <div className={`usage-limit__status-badge ${usageLimitData?.usageLimitRequired ? "usage-limit__status-yes" : "usage-limit__status-no"}`}>
                      {usageLimitData?.usageLimitRequired ? "Yes" : "No"}
                    </div>
                  )}
                </div>

                {/* BP Limits Section - Show ONLY if there are BP limits OR in edit mode with isUsageLimitRequired true */}
                {(bpLimits.length > 0 || (isEditMode && isUsageLimitRequired)) && (
                  <>
                    {renderLimitSection("BP", bpLimits, bpHandlers, canAddMoreBpLimits(), "BP")}
                  </>
                )}
                
                {/* Tag Limits Section - Show ONLY for NON-WALLET accounts AND if there are tag limits OR in edit mode */}
                {!isWalletAccount && (tagLimits.length > 0 || (isEditMode && isUsageLimitRequired)) && (
                  <>
                    {(bpLimits.length > 0 || (isEditMode && isUsageLimitRequired)) && (
                      <div className="usage-limit__divider"></div>
                    )}
                    {renderLimitSection("TAG", tagLimits, tagHandlers, canAddMoreTagLimits(), "TAG")}
                  </>
                )}
              </div>

              {/* Show Update/Configure buttons only if user can perform actions */}
              {!isEditMode && (bpLimits.length > 0 || tagLimits.length > 0) && canPerformActions && (
                <div className="usage-limit__action-section">
                  <button className="usage-limit__update-btn" onClick={handleEditClick}>
                    Update Limits
                  </button>
                </div>
              )}
              
              {!isEditMode && bpLimits.length === 0 && tagLimits.length === 0 && canPerformActions && (
                <div className="usage-limit__action-section">
                  <button className="usage-limit__update-btn" onClick={handleEditClick}>
                    Configure Limits
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitComponent;